import React, { Dispatch, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion, Variants } from 'framer-motion';
import { useLoaderData } from 'react-router-dom';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

import { ANIMATION_SPEED } from '../../features/rapidstor/animations';
import { isDescriptorAvailable, sortUnitTypes } from '../../features/rapidstor/helpers';

import classes from './PopCard.module.scss';

import PopCard from './PopCard';
interface Props {
    descriptor: Descriptor;
    desktop?: boolean;
    setCarouselHidden?: Dispatch<React.SetStateAction<boolean>>;
    onContinueEvent: (unitType: UnitType) => void;
}

export default function Stack({
    descriptor,
    desktop,
    onContinueEvent,
    setCarouselHidden,
}: Props) {
    const location = useLoaderData() as Location;
    const [stackHeight, setStackHeight] = useState<string | number>(300);
    const [cards, setCards] = useState<UnitType[]>();
    const [cardsOpen, setCardsOpen] = useState<boolean[]>([true, false]);
    const [cardHeights, setCardHeights] = useState<number[]>([]);
    const [expandedCard, setExpandedCard] = useState<number>(-1);
    const cardClasses = [classes.card_0, classes.card_1, classes.card_2];
    const defaultCardHeight = 260;
    const closedCardHeight = 86;
    const [initialCardHeights, setInitialCardHeights] = useState<number[]>([]);
    const initialCardRefs = useRef<(HTMLDivElement | null)[]>([]);

    useLayoutEffect(() => {
        if (initialCardHeights) {
            setInitialCardHeights([]);
        }
        const timeout = setTimeout(() => {
            const heights: number[] = [];
            initialCardRefs.current?.forEach((ele) => {
                if (ele) {
                    ele.style.height = 'auto';
                    const height = ele.scrollHeight || defaultCardHeight;
                    heights.push(height);
                }
            });
            if (heights.length === 1) {
                heights.push(heights[0]);
            }
            setInitialCardHeights(heights);
        }, 100);

        setTimeout(() => {
            setCard1State('cardLoaded');
            setCard2State('cardLoaded');
        }, 400);

        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [descriptor]);

    /**
     * ANIMATION STATE
     */
    const [card1State, setCard1State] = useState<string>('');
    const [card2State, setCard2State] = useState<string>('');
    const [containerWidth, setContainerWidth] = useState<number>(window.innerWidth);

    useEffect(() => {
        const container = document.getElementById('book-unit');
        const width = container?.getBoundingClientRect().width;
        setContainerWidth(width || 0);
        window.addEventListener('resize', () => {
            const container = document.getElementById('book-unit');
            const width = container?.getBoundingClientRect().width;
            setContainerWidth(width || 0);
        });
        return () => {
            window.addEventListener('resize', () => {
                const container = document.getElementById('book-unit');
                const width = container?.getBoundingClientRect().width;
                setContainerWidth(width || 0);
            });
        };
    }, []);

    /**
     * ANIMATION VARIANTS
     */
    const cardStackVariants: Variants = {
        card1Init: {
            opacity: desktop ? 1 : 0,
            position: 'absolute',
        },
        card2Init: {
            opacity: desktop ? 1 : 0,
            position: 'absolute',
        },
        cardLoaded: {
            opacity: 1,
        },
        changing: {
            opacity: 0,
        },
    };

    const cardStackVariantsDesktop: Variants = {
        card1Init: {
            opacity: desktop ? 1 : 0,
            position: 'absolute',
        },
        card2Init: {
            opacity: desktop ? 1 : 0,
            position: 'absolute',
        },
        cardLoaded: {
            opacity: 1,
        },
        card1Selected: {
            position: 'absolute',
            backgroundColor: 'var(--rs-theme)',
            width: 420,
            zIndex: 5,
            top: 0,
            left: containerWidth / 2 - 210,
        },
        card2Selected: {
            position: 'absolute',
            backgroundColor: 'var(--rs-theme)',
            width: 420,
            zIndex: 5,
            top: 0,
            left: containerWidth / 2 - 210,
        },
        fadeOut: {
            opacity: 0,
            zIndex: 0,
        },
    };

    useEffect(() => {
        getUnitType(descriptor);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [descriptor, initialCardHeights]);

    const getUnitType = (descriptor: Descriptor) => {
        if (!descriptor.unitTypes) {
            return null;
        }
        const cardsList: UnitType[] = [];

        setCardsOpen([true, false]);
        setStackHeight(500);

        const unitType = sortUnitTypes(descriptor.unitTypes, location)[0];

        const copiedUnitType = unitType; // stupid readonly
        cardsList.push({ ...copiedUnitType, deal: undefined });

        for (const deal of unitType.deals) {
            const newUnitType = { ...unitType, deal: deal };
            cardsList.push(newUnitType);
        }

        // @feature hideStandardPrices
        // Hide standard prices if there are deals
        if (location.rapidstor.hideStandardPrices && cardsList.length > 1) {
            cardsList.splice(0, 1);
        }

        setCards(cardsList.length > 2 ? cardsList.slice(1, 3) : cardsList);

        const firstLoadCardHeights: number[] = initialCardHeights.map((height, index) => {
            if (index === 0) {
                return height + (cardsList.length > 1 ? closedCardHeight : 0);
            } else {
                return closedCardHeight;
            }
        });

        setCardHeights(firstLoadCardHeights);
    };

    const doOpenCard = (id: number) => {
        setStackHeight('100%');
        if (desktop) {
            if (id === 0) {
                setCard1State('card1Selected');
                setCard2State('fadeOut');
            } else {
                setCard2State('card2Selected');
                setCard1State('fadeOut');
            }
        } else {
            setCard1State('cardLoaded');
        }
        if (desktop) {
            setTimeout(() => {
                navigateScreen(id);
            }, 300);
        } else {
            navigateScreen(id);
        }
    };

    const navigateScreen = (id: number) => {
        const array = cardHeights;
        for (const a in array) {
            if (parseInt(a) === id) {
                setExpandedCard(id);
                setTimeout(() => {
                    if (!cards || !cards[id]) {
                        return null;
                    }
                    onContinueEvent(cards[id]);
                }, 300);
            }
        }
    };

    const toggleOpen = (e: React.MouseEvent<HTMLElement>) => {
        const clickedIndex = parseInt(e.currentTarget.getAttribute('data-index') ?? '');
        if (!cardsOpen[clickedIndex]) {
            changeCardHeights(clickedIndex);
        }
    };

    const changeCardHeights = (clickedIndex: number) => {
        const x = cardsOpen;

        const totalClosedCards = cardsOpen.filter((x) => !x).length;
        const updatedCardHeights: number[] = [];

        for (const i in x) {
            if (parseInt(i) === clickedIndex) {
                x[i] = !x[i];
            } else {
                x[i] = false;
            }
        }

        // x is the expected open state of the card. Eg if we're expecting the card to be active, x[i] will be true
        for (const i in x) {
            if (clickedIndex === 0) {
                if (x[i]) {
                    updatedCardHeights.push(closedCardHeight + initialCardHeights[i]);
                } else {
                    updatedCardHeights.push(closedCardHeight * (x.length - parseInt(i)));
                }
            } else {
                if (parseInt(i) === 0) {
                    updatedCardHeights.push(
                        totalClosedCards * closedCardHeight +
                        initialCardHeights[clickedIndex]
                    );
                } else {
                    updatedCardHeights.push(initialCardHeights[i]);
                }
            }
        }

        setCardHeights(updatedCardHeights);
        setCardsOpen(x);
    };

    return (
        <div
            className={
                (desktop ? classes.stack_desktop : classes.stack) +
                (cards && cards.find(card => card.deal) ? ' has_deal' : ' no_deal')
            }
            style={{ height: desktop ? 'auto' : stackHeight }}
            id='stack'
        >
            {cards?.map((card, index) => {
                const isDeal = card.deal ? true : false;
                const isAvailable = isDescriptorAvailable(descriptor);
                return (
                    <div
                        key={`${index}_card_idx`}
                        className={`${classes.card_wrapper}${isAvailable ? ' available' : ' not_available'}`}
                        id={`${cards.length > 1 && !isDeal ? 'non-deal-card' : 'deal-card'
                            }`}
                        data-deal={isDeal ? 'true' : 'false'}
                        data-only-card={cards.length === 1 ? 'true' : 'false'}
                    >
                        <div
                            key={`${index}_card_ref`}
                            id={`${index}_card_ref`}
                            ref={(el) => (initialCardRefs.current[index] = el)}
                            data-index={index}
                            className={`${classes.card} ${cardClasses[index]}`}
                            style={{
                                height: desktop ? 'auto' : cardHeights[index] + 'px',
                                opacity: 0,
                                display: !desktop
                                    ? cardHeights.length > 0
                                        ? 'none'
                                        : 'block'
                                    : 'none',
                            }}
                        >
                            <PopCard
                                card={card}
                                index={index}
                                isDeal={isDeal}
                                isOpen={cardsOpen[index]}
                                descriptor={descriptor}
                                onContinueEvent={onContinueEvent}
                                setCarouselHidden={setCarouselHidden}
                                doOpenCard={doOpenCard}
                            />
                        </div>
                        <motion.div
                            layout={true}
                            variants={
                                desktop ? cardStackVariantsDesktop : cardStackVariants
                            }
                            initial={index === 1 ? 'card2Init' : 'card1Init'}
                            animate={index === 1 ? card2State : card1State}
                            transition={{
                                duration: ANIMATION_SPEED,
                            }}
                            key={`${index}_card_idx`}
                            id={`${index}_card_idx`}
                            data-index={index}
                            onClick={toggleOpen}
                            className={`${classes.card} ${cardClasses[index]} ${expandedCard === index && !desktop
                                ? classes.expandedCard
                                : ''
                                }`}
                            style={{
                                height: desktop ? 'auto' : cardHeights[index] + 'px',
                            }}
                        >
                            <PopCard
                                card={card}
                                index={index}
                                isDeal={isDeal}
                                isOpen={cardsOpen[index]}
                                descriptor={descriptor}
                                onContinueEvent={onContinueEvent}
                                setCarouselHidden={setCarouselHidden}
                                doOpenCard={doOpenCard}
                            />
                        </motion.div>
                    </div>
                );
            })}
        </div>
    );
}
